import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { ButtonCellRenderer } from "@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { ButtonConfig } from "@template-ui/types";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ColDef } from "ag-grid-community";
import {
  ChangeSetEntry,
  ChangeSetImpactSummaryRow,
  ChangeSetPreview,
  ChangeSetWorkspaceResult,
  toImpactSummary,
} from "./platform-change-set-workspace.models";
import { PlatformChangeSetWorkspaceService } from "./platform-change-set-workspace.service";

interface ChangeSetPreviewFormValues {
  configurationCode: string;
}

interface ChangeSetReasonFormValues {
  reason: string;
}

const DEFAULT_PREVIEW_FORM: ChangeSetPreviewFormValues = {
  configurationCode: "phms-runtime",
};

@Component({
  selector: "phms-platform-change-set-workspace",
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./platform-change-set-workspace.component.html",
  styleUrl: "./platform-change-set-workspace.component.scss",
})
export class PlatformChangeSetWorkspaceComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspaceService = inject(PlatformChangeSetWorkspaceService);

  @ViewChild("previewForm")
  private previewForm?: EzFormComponent;

  @ViewChild("reasonForm")
  private reasonForm?: EzFormComponent;

  readonly isLoading = signal(false);
  readonly isApplying = signal(false);
  readonly previewResult = signal<ChangeSetWorkspaceResult<ChangeSetPreview> | null>(null);
  readonly selectedEntry = signal<ChangeSetEntry | null>(null);
  readonly actionMessage = signal<string>("");

  readonly preview = computed(() => this.previewResult()?.data ?? null);
  readonly entries = computed(() => this.preview()?.entries ?? []);
  readonly impactSummary = computed<ChangeSetImpactSummaryRow[]>(() => toImpactSummary(this.entries()));
  readonly hasLoaded = computed(() => this.previewResult() !== null);

  readonly previewFormConfig: InputConfig[] = this.buildPreviewFormConfig();
  readonly reasonFormConfig: InputConfig[] = this.buildReasonFormConfig();
  readonly entryColumnDefs: ColDef[] = this.buildEntryColumnDefs();
  readonly impactColumnDefs: ColDef[] = this.buildImpactColumnDefs();

  readonly previewActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: "loadPreview",
      buttonText: this.isLoading() ? "Loading..." : "Load Change Set",
      disabled: this.isLoading() || this.isApplying(),
    },
    {
      id: "resetWorkspace",
      buttonText: "Reset",
      severity: "secondary",
      disabled: this.isLoading() || this.isApplying(),
    },
  ]);

  readonly applyActionButtons = computed<ButtonConfig[]>(() => {
    const entry = this.selectedEntry();
    return [
      {
        id: "publishChange",
        buttonText: this.isApplying() ? "Publishing..." : "Publish",
        disabled: this.isApplying() || !entry?.canPublish,
      },
      {
        id: "rollbackChange",
        buttonText: this.isApplying() ? "Rolling back..." : "Rollback",
        severity: "secondary",
        disabled: this.isApplying() || !entry?.canRollback,
      },
    ];
  });

  previewFormValues: ChangeSetPreviewFormValues = { ...DEFAULT_PREVIEW_FORM };
  reasonFormValues: ChangeSetReasonFormValues = { reason: "" };

  onPreviewActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "loadPreview":
        this.loadPreview();
        break;
      case "resetWorkspace":
        this.resetWorkspace();
        break;
      default:
        break;
    }
  }

  onApplyActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "publishChange":
        this.applySelectedChange("publish");
        break;
      case "rollbackChange":
        this.applySelectedChange("rollback");
        break;
      default:
        break;
    }
  }

  selectEntry(entry: ChangeSetEntry): void {
    this.selectedEntry.set(entry);
    this.actionMessage.set("");
  }

  loadPreview(): void {
    if (!this.ensureFormIsValid(this.previewForm)) {
      return;
    }

    this.isLoading.set(true);
    this.previewResult.set(null);
    this.selectedEntry.set(null);
    this.actionMessage.set("");

    this.workspaceService
      .loadPreview({ configurationCode: this.previewFormValues.configurationCode.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.previewResult.set(result);
        this.isLoading.set(false);
      });
  }

  resetWorkspace(): void {
    this.previewFormValues = { ...DEFAULT_PREVIEW_FORM };
    this.reasonFormValues = { reason: "" };
    this.previewForm?.clearErrors();
    this.reasonForm?.clearErrors();
    this.isLoading.set(false);
    this.isApplying.set(false);
    this.previewResult.set(null);
    this.selectedEntry.set(null);
    this.actionMessage.set("");
  }

  formatMessage(result: ChangeSetWorkspaceResult<unknown> | null): string {
    if (!result) {
      return "";
    }

    if (result.state === "resolved") {
      return "Resolved";
    }

    return result.error?.messages.join(" ") ?? "Change-set workspace unavailable.";
  }

  private applySelectedChange(action: "publish" | "rollback"): void {
    const entry = this.selectedEntry();
    if (!entry) {
      return;
    }

    if ((action === "publish" && !entry.canPublish) || (action === "rollback" && !entry.canRollback)) {
      this.actionMessage.set("This change is authoring-only and cannot be published or rolled back here.");
      return;
    }

    if (!this.ensureFormIsValid(this.reasonForm) || entry.entryId == null) {
      return;
    }

    const reason = this.reasonFormValues.reason.trim();
    const action$ = action === "publish"
      ? this.workspaceService.publishTerminologyEntry(entry.entryId)
      : this.workspaceService.rollbackTerminologyEntry(entry.entryId);

    this.isApplying.set(true);
    action$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      this.isApplying.set(false);
      if (result.state === "resolved") {
        this.actionMessage.set(`${action === "publish" ? "Published" : "Rolled back"} ${entry.subjectCode} (reason: ${reason}).`);
        this.loadPreview();
      } else {
        this.actionMessage.set(result.error?.messages.join(" ") ?? "The change could not be applied.");
      }
    });
  }

  private ensureFormIsValid(form: EzFormComponent | undefined): boolean {
    const formGroup = form?.theFormGroup;
    if (!formGroup || formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    return false;
  }

  private buildPreviewFormConfig(): InputConfig[] {
    return [
      {
        name: "configurationCode",
        type: EzFormInputType.TEXT,
        label: "Configuration",
        row: 1,
        width: "fill",
        validation: [Validators.required],
        validationMessages: {
          required: "Configuration code is required.",
        },
      },
    ];
  }

  private buildReasonFormConfig(): InputConfig[] {
    return [
      {
        name: "reason",
        type: EzFormInputType.TEXT,
        label: "Reason",
        row: 1,
        width: "fill",
        validation: [Validators.required],
        validationMessages: {
          required: "A reason is required before publishing or rolling back.",
        },
      },
    ];
  }

  private buildEntryColumnDefs(): ColDef[] {
    return [
      { field: "domainCode", headerName: "Domain", width: 150 },
      { field: "changeKind", headerName: "Change", width: 110 },
      { field: "subjectCode", headerName: "Subject", flex: 1, minWidth: 160 },
      {
        field: "beforeValue",
        headerName: "Before",
        flex: 1,
        minWidth: 160,
        valueFormatter: ({ value }) => (value as string | null | undefined) ?? "(none)",
      },
      {
        field: "afterValue",
        headerName: "After",
        flex: 1,
        minWidth: 160,
        valueFormatter: ({ value }) => (value as string | null | undefined) ?? "(none)",
      },
      { field: "impactedArea", headerName: "Impacted Area", flex: 2, minWidth: 240 },
      { field: "versionStatusCode", headerName: "Status", width: 110 },
      {
        headerName: "",
        width: 90,
        maxWidth: 90,
        sortable: false,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: "Review",
          onClick: (params: { rowData: ChangeSetEntry }) => this.selectEntry(params.rowData),
        },
      },
    ];
  }

  private buildImpactColumnDefs(): ColDef[] {
    return [
      { field: "domainCode", headerName: "Domain", flex: 1, minWidth: 180 },
      { field: "changeCount", headerName: "Pending Changes", width: 170 },
    ];
  }
}
